import React, { useRef } from "react"

import { OTPInput } from "input-otp"
import { type Control, useController } from "react-hook-form"

import type { IIndexable } from "@common-shared/types/general.types.ts"

import { InputErrorElement } from "@front-shared/elements/inputError.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { useScrollErroredInputIntoView } from "@front-shared/hooks/scrollErroredInputtIntoView.hook.ts"

import { AUTH_CONFIG } from "../config/auth.config"

export const OtpInputElement: React.FunctionComponent<
    IOtpInputElementProps
> = props => {
    const containerRef = useRef<HTMLDivElement>(null)
    const {
        field,
        fieldState: { error },
    } = useController({
        control: props.reactHookControl,
        name: props.name,
        rules: props.reactHookValidations,
    })

    useScrollErroredInputIntoView(
        props.name,
        containerRef,
        {
            [props.name]: error,
        },
        props.ignoreScrollIntoViewOnError,
    )

    return (
        <div
            ref={containerRef}
            className={`
                relative
                ${props.className}
            `}
            style={{
                width: "fit-content",
            }}
        >
            <OTPInput
                {...field}
                autoFocus={props.autoFocus}
                maxLength={props.maxLength || AUTH_CONFIG.mfaCodeLength}
                containerClassName="group flex sm:gap-[12px] gap-[24px] items-center has-[:disabled]:opacity-30"
                render={({ slots }) =>
                    slots.map((slot, idx) => (
                        <div
                            key={idx}
                            className={`sm:h-[48px] sm:w-[40px] h-[56px] w-[48px] rounded-[6px] border border-brand-warmShadow bg-brand-white flex items-center justify-center ${
                                slot.isActive
                                    ? "border-[2px] !border-brand-primary"
                                    : ""
                            }`}
                        >
                            <PrimaryText typography="text">
                                {slot.char || slot.placeholderChar}
                            </PrimaryText>
                        </div>
                    ))
                }
            />

            {error?.message && !props.hideErrors ? (
                <InputErrorElement text={error.message} />
            ) : null}
        </div>
    )
}

interface IOtpInputElementProps {
    name: string
    className?: string
    autoFocus?: boolean
    ignoreScrollIntoViewOnError?: boolean
    reactHookControl?: Control
    reactHookValidations?: IIndexable
    hideErrors?: boolean
    maxLength?: number
}
